import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { DeviceLink } from "../devices/DeviceLink";
import { Badge } from "../ui/Badge";
import { DescriptionList } from "../ui/DescriptionList";
import { JsonViewer } from "../ui/JsonViewer";
import { Modal } from "../ui/Modal";
import { ApiError } from "../../lib/api";
import { networkViolationsApi } from "../../lib/api/network/violations";
import { deviceNetworkPolicyApi } from "../../lib/api/network/policy";
import { formatDateTime } from "../../lib/format";
import { formatMessage, t } from "../../lib/i18n";

interface ViolationDetailModalProps {
  eventId: string | null;
  onClose: () => void;
}

interface AccordionSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

function AccordionSection({ title, defaultOpen = false, children }: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="violation-detail__section">
      <button
        type="button"
        className="violation-detail__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{title}</span>
        <span className="violation-detail__chevron" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open && <div className="violation-detail__panel">{children}</div>}
    </section>
  );
}

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function ViolationDetailModal({ eventId, onClose }: ViolationDetailModalProps) {
  const detail = useQuery({
    queryKey: ["violations", "detail", eventId],
    queryFn: () => networkViolationsApi.get(eventId!),
    enabled: Boolean(eventId),
  });

  const event = detail.data?.violation ?? detail.data?.event;
  const analysis = detail.data?.analysis;
  const violation = analysis?.violation ?? event ?? null;

  const policyQuery = useQuery({
    queryKey: ["network", "policy", event?.device_id, event?.policy_version],
    queryFn: () =>
      deviceNetworkPolicyApi.get(event!.device_id, event!.policy_version),
    enabled: Boolean(event?.device_id && event?.policy_version),
  });

  return (
    <Modal
      open={Boolean(eventId)}
      onClose={onClose}
      size="lg"
      title={t("violations.detail.title")}
    >
      {detail.isLoading && (
        <p style={{ color: "var(--text-muted)" }}>{t("common.loading")}</p>
      )}
      {detail.error && (
        <p style={{ color: "var(--danger)" }}>{translateError(detail.error)}</p>
      )}
      {event && (
        <div className="violation-detail">
          <AccordionSection title={t("violations.detail.sectionSummary")} defaultOpen>
            <DescriptionList
              columns={2}
              items={[
                {
                  labelKey: "violations.column.time",
                  value: (
                    <span className="mono">{formatDateTime(event.attempted_at)}</span>
                  ),
                },
                {
                  labelKey: "violations.column.device",
                  value: (
                    <DeviceLink
                      deviceId={event.device_id}
                      deviceUid={event.device_uid}
                    />
                  ),
                },
                {
                  labelKey: "violations.column.domain",
                  value: <code className="mono">{event.destination}</code>,
                },
                {
                  labelKey: "violations.column.kind",
                  value: <span className="mono">{event.pool_kind}</span>,
                },
                {
                  labelKey: "violations.column.vuln",
                  value: event.url_pool_vuln_status,
                },
              ]}
            />
          </AccordionSection>

          <AccordionSection title={t("violations.detail.sectionReason")} defaultOpen>
            {analysis?.block_reason ? (
              <>
                <div className="violation-detail__reason">
                  <Badge tone="danger">{analysis.block_reason.label}</Badge>
                  {analysis.block_reason.source && (
                    <span className="mono" style={{ color: "var(--text-muted)" }}>
                      {analysis.block_reason.source}
                    </span>
                  )}
                </div>
                {analysis.matched_rule && (
                  <DescriptionList
                    columns={2}
                    items={[
                      {
                        labelKey: "violations.detail.matchedList",
                        value: (
                          <span className="mono">{analysis.matched_rule.list}</span>
                        ),
                      },
                      {
                        labelKey: "violations.detail.matchedSource",
                        value: (
                          <span className="mono">
                            {analysis.matched_rule.source || "—"}
                          </span>
                        ),
                      },
                    ]}
                  />
                )}
              </>
            ) : (
              <p style={{ color: "var(--text-muted)" }}>
                {t("violations.detail.noReason")}
              </p>
            )}
          </AccordionSection>

          <AccordionSection title={t("violations.detail.sectionPolicy")}>
            {analysis?.policy ? (
              <DescriptionList
                columns={2}
                items={[
                  {
                    labelKey: "violations.detail.policyName",
                    value: analysis.policy.name ? (
                      <Link to="/rules">{analysis.policy.name}</Link>
                    ) : (
                      t("violations.detail.noNamedPolicy")
                    ),
                  },
                  {
                    labelKey: "violations.detail.defaultAction",
                    value: (
                      <span className="mono">{analysis.policy.default_action}</span>
                    ),
                  },
                  {
                    labelKey: "violations.detail.ruleCounts",
                    value: (
                      <span className="mono">
                        {formatMessage("violations.detail.allowBlockCounts", {
                          allow: String(analysis.policy.allow_count),
                          block: String(analysis.policy.block_count),
                        })}
                      </span>
                    ),
                  },
                ]}
              />
            ) : (
              <p style={{ color: "var(--text-muted)" }}>
                {t("violations.detail.policyMissing")}
              </p>
            )}
          </AccordionSection>

          {(violation || policyQuery.data) && (
            <AccordionSection title={t("violations.detail.sectionTechnical")}>
              {analysis?.policy && (
                <DescriptionList
                  columns={1}
                  items={[
                    {
                      labelKey: "violations.detail.policyVersion",
                      value: (
                        <span className="mono">
                          {formatMessage("violations.blockReason.versionTooltip", {
                            version: String(analysis.policy.version),
                          })}
                        </span>
                      ),
                    },
                    {
                      labelKey: "violations.detail.policyHash",
                      value: (
                        <code className="mono violation-detail__hash">
                          {analysis.policy.hash}
                        </code>
                      ),
                    },
                  ]}
                />
              )}
              {violation && (
                <DescriptionList
                  columns={2}
                  items={[
                    {
                      labelKey: "violations.detail.resolvedIp",
                      value: violation.resolved_ip || "—",
                    },
                    {
                      labelKey: "violations.detail.vpnMode",
                      value: violation.protection_policy_mode || "—",
                    },
                    {
                      labelKey: "violations.detail.protocol",
                      value: violation.protocol
                        ? `${violation.protocol}${violation.port ? `:${violation.port}` : ""}`
                        : "—",
                    },
                    {
                      labelKey: "violations.detail.matchedRuleId",
                      value: violation.matched_rule_id ? (
                        <code className="mono">{violation.matched_rule_id}</code>
                      ) : (
                        "—"
                      ),
                    },
                  ]}
                />
              )}
              {policyQuery.data && (
                <JsonViewer value={policyQuery.data.payload} defaultCollapsed />
              )}
            </AccordionSection>
          )}
        </div>
      )}
    </Modal>
  );
}
